import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, X } from 'lucide-react';
import constant from '../../../constant';
import ChangePasswordForm from '../../ui/changePasswordForm';

const FloatingAlert = ({ message, onClose }) => {
    useEffect(() => {
        const timer = setTimeout(() => {
            onClose();
        }, 3000);
        return () => clearTimeout(timer);
    }, [onClose]);

    return (
        <div className="fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md shadow-md z-50">
            <div className="flex items-center justify-between">
                <span className="block sm:inline">{message}</span>
                <button onClick={onClose} className="ml-4 focus:outline-none">
                    <X className="h-4 w-4 text-green-700 opacity-75 hover:opacity-100" />
                </button>
            </div>
        </div>
    );
};

const ChangePassword = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [formData, setFormData] = useState({
        oldPassword: '',
        newPassword: '',
    });

    const handleCancel = () => {
        const isFirstLogin = localStorage.getItem('is_first_login') === 'true';
        if (isFirstLogin) {
            // Prevent navigation away if it's the first login
            setError('You must change your password before proceeding.');
        } else {
            navigate(-1);
        }
    };

    const handleSubmit = async (formData) => {

        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        const token = localStorage.getItem('token');
        const hospitalId = localStorage.getItem('hospitalId');
        const userId = localStorage.getItem('userId');

        try {
            const response = await fetch(`${constant.REACT_APP_API_URL}/hospital/${hospitalId}/user/${userId}/change-password`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    current_password: formData.oldPassword,
                    password: formData.newPassword,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Failed to change password. Status: ${response.status}`);
            }

            // Update is_first_login in localStorage
            localStorage.setItem('is_first_login', 'false');
            window.dispatchEvent(new Event('storage')); // Trigger storage event to update App state

            setSuccessMessage('Password changed successfully!');
            setFormData({
                oldPassword: '',
                newPassword: '',
            });

            // Redirect to dashboard after a short delay to show success message
            setTimeout(() => {
                navigate('/dashboard');
            }, 1500);

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSuccess = () => {
        setSuccessMessage(null);
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-cover bg-center gradient">
            {successMessage && (
                <FloatingAlert message={successMessage} onClose={handleCloseSuccess} />
            )}

            <div className="w-full flex justify-center md:px-12 px-4">
                <div className="bg-none md:p-10 p-8 rounded-xl shadow-2xl w-full max-w-md backdrop-blur-sm bg-opacity-95 border border-gray-100">
                    <div className="flex items-center justify-center mb-4">
                        <h2 className="text-2xl font-bold text-center text-gray-200">Change Password</h2>
                    </div>

                    {error && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6" role="alert">
                            <strong className="font-bold">Error!</strong>
                            <span className="block sm:inline ml-1">{error}</span>
                        </div>
                    )}

                    {loading ? (
                        <div className="flex items-center justify-center">
                            <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
                        </div>
                    ) : (
                        <ChangePasswordForm
                            initialFormData={formData}
                            onSubmit={handleSubmit}
                            onCancel={handleCancel}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChangePassword;